import React, { useRef } from 'react';
import {
  Input,
  Button,
  Space,
  Typography,
  Badge,
  Tooltip
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  ExpandOutlined,
  CompressOutlined
} from '@ant-design/icons';
import { getDescriptionStats } from '../utils';
import { DESCRIPTION_CONFIG } from '../constants';
import { message } from 'antd';

const { TextArea } = Input;
const { Text } = Typography;

const DescriptionEditor = ({
  value,
  onChange,
  mode = 'edit',
  expanded = false,
  onModeChange,
  onExpandedChange,
  style = {}
}) => {
  const textAreaRef = useRef(null);
  const descriptionStats = getDescriptionStats(value);

  const formatDescriptionPreview = (text) => {
    if (!text) return 'No description provided.';

    return text.split('\n').map((line, index) => (
      <div key={index} style={{ marginBottom: '8px', lineHeight: '1.5' }}>
        {line || <br />}
      </div>
    ));
  };

  return (
    <div style={{
      border: '1px solid #f0f0f0',
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      ...style
    }}>
      {/* Header */}
      <div style={{
        background: '#fafafa',
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text strong>Description</Text>
          {descriptionStats.characters > 0 && (
            <Badge
              count={`${descriptionStats.lines} lines • ${descriptionStats.words} words • ${descriptionStats.characters} chars`}
              style={{
                background: 'transparent',
                color: '#8c8c8c',
                fontSize: '11px',
                fontWeight: 'normal'
              }}
            />
          )}
        </div>

        <Space>
          {mode === 'edit' ? (
            <Tooltip title="Preview Mode">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => onModeChange('preview')}
                style={{ borderRadius: '4px' }}
              >
                Preview
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Edit Mode">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => onModeChange('edit')}
                style={{ borderRadius: '4px' }}
              >
                Edit
              </Button>
            </Tooltip>
          )}

          <Tooltip title={expanded ? "Collapse" : "Expand"}>
            <Button
              size="small"
              icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => onExpandedChange(!expanded)}
              style={{ borderRadius: '4px' }}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {mode === 'edit' ? (
          <TextArea
            ref={textAreaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={expanded ? DESCRIPTION_CONFIG.EXPANDED_ROWS : DESCRIPTION_CONFIG.COLLAPSED_ROWS}
            placeholder="Describe the task in detail..."
            style={{
              borderRadius: '6px',
              resize: 'vertical',
              minHeight: expanded ? '400px' : '120px',
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: '13px',
              lineHeight: '1.5'
            }}
            showCount
            maxLength={DESCRIPTION_CONFIG.MAX_LENGTH}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.shiftKey) {
                // Shift+Enter برای خط جدید
                return;
              }
              if (e.key === 'Enter' && e.ctrlKey) {
                // Ctrl+Enter برای ذخیره
                e.preventDefault();
                message.success('Changes saved!');
              }
            }}
          />
        ) : (
          <div
            style={{
              background: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              padding: '16px',
              minHeight: expanded ? '400px' : '120px',
              maxHeight: expanded ? '500px' : '200px',
              overflowY: 'auto',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#333'
            }}
          >
            {formatDescriptionPreview(value)}
          </div>
        )}
      </div>

      {/* Tips */}
      <div style={{
        background: '#e6f7ff',
        padding: '8px 16px',
        borderTop: '1px solid #91d5ff',
        fontSize: '11px',
        color: '#0050b3'
      }}>
        💡 <Text strong>Tips:</Text> Use Markdown-style formatting for better readability.
        Press Shift+Enter for new line, Ctrl+Enter to save.
      </div>
    </div>
  );
};

export default DescriptionEditor;
